import React, { useState, FormEvent } from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { addContractor } from 'api/contractors_management';
import { Button } from './ui/Button';
import { colors } from '../theme/colors';

interface AddContractorFormProps {
  onSuccess: () => void;
}

const AddContractorForm: React.FC<AddContractorFormProps> = ({ onSuccess }) => {
  const [username, setUsername] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    try {
      setIsLoading(true);
      await addContractor(username, firstName, lastName, password, email);
      setSuccess(true);

      // Reset form after short delay
      setTimeout(() => {
        setUsername('');
        setFirstName('');
        setLastName('');
        setPassword('');
        setEmail('');
        setSuccess(false);
        onSuccess();
      }, 1500);
    } catch (err) {
      setError('Не удалось добавить контрактора. Попробуйте снова.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          mb: 3,
          fontWeight: 600,
          color: colors.text.primary,
        }}
      >
        Добавить контрактора
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{
            mb: 3,
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(-10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          Контрактор успешно добавлен!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          label="Имя пользователя"
          fullWidth
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          disabled={isLoading || success}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Имя"
          fullWidth
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          disabled={isLoading || success}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Фамилия"
          fullWidth
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
          disabled={isLoading || success}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading || success}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Email"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading || success}
          sx={{ mb: 3 }}
        />

        <Button type="submit" variant="gradient" fullWidth disabled={isLoading || success} loading={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
              Добавление...
            </>
          ) : (
            'Добавить контрактора'
          )}
        </Button>
      </form>
    </Box>
  );
};

export default AddContractorForm;